import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import { Nullable } from 'primeng/ts-helpers';
import { AuthService } from '../../../../core/services/auth.service';
import { Authorities } from '../../../../shared/enums/authorities.enum';
import { LocationType } from '../../../../shared/enums/location-type.enum';
import { Modules } from '../../../../shared/enums/modules.enum';
import { SearchEquipment } from '../contract/requests/search-equipment.contract';
import { Status } from '../contract/status.contract';
import { ViewEquipment } from '../contract/view-equipment.contract';
import { EquipmentStatusCategory } from '../enums/equipment-status-category.enum';
import { TelematicsUnitType } from '../enums/telematics-unit-type.enum';
import { EquipmentTelematicsUnitResolver } from '../resolver/equipment-telematics-unit-resolver';

type EquipmentWithStatus = { status: Status };

@Injectable({ providedIn: 'root' })
export class EquipmentCheckerService {
  private authService = inject(AuthService);

  public isSharedEquipment(entity: ViewEquipment | SearchEquipment | { customerId: string } | Nullable): boolean {
    return Boolean(entity) && entity?.customerId !== this.authService.userCustomerId;
  }

  public isActiveEquipment(equipment: EquipmentWithStatus | Nullable): boolean {
    return Boolean(
      equipment &&
      (equipment.status?.category === EquipmentStatusCategory.OPERATIONAL ||
        equipment.status?.category === EquipmentStatusCategory.OPERATIONAL_WITH_RESTRICTIONS ||
        equipment.status?.category === EquipmentStatusCategory.NOT_OPERATIONAL),
    );
  }

  public isInactiveEquipment(equipment: EquipmentWithStatus | Nullable): boolean {
    return equipment?.status?.category === EquipmentStatusCategory.NOT_AVAILABLE;
  }

  public isViewEquipmentLinkedToTelematicsUnit(equipment: ViewEquipment | Nullable): boolean {
    return (equipment?.assignedTelematicsUnits || []).length > 0
  }

  public canViewEquipmentHaveActivityStatus(equipment: ViewEquipment | Nullable): boolean {
    return equipment?.assignedTelematicsUnits?.length === 1 &&
      !EquipmentTelematicsUnitResolver.isBeaconTelematicUnit(equipment?.assignedTelematicsUnits[0].key)
  }

  public isSearchEquipmentLinkedToTelematicsUnit(equipment: SearchEquipment): boolean {
    return equipment != null && equipment.assignedTelematicUnits > 0;
  }

  public hasEquipmentCurrentTelematicsData(equipment: ViewEquipment | SearchEquipment | Nullable): boolean {
    return <boolean>(
      (equipment &&
        equipment.lastTelematicsUpdate != null &&
        moment(equipment.lastTelematicsUpdate).isAfter(moment().subtract(7, 'days')))
    );
  }

  public isTransportVehicle(equipment: ViewEquipment | SearchEquipment): boolean {
    return equipment?.transportTypes?.length > 0;
  }

  public isNotAvailableAnymore(category: EquipmentStatusCategory | Nullable): boolean {
    return category === EquipmentStatusCategory.NOT_AVAILABLE;
  }

  public isTransferredToProject(equipment: ViewEquipment): boolean {
    return equipment?.transferDestinationType === LocationType.PROJECT;
  }

  public hasActiveTransferRequest(equipment: ViewEquipment): boolean {
    return Boolean(equipment?.pendingTransferRequestId);
  }

  public canLogEquipmentEvents(equipment: ViewEquipment | Nullable): boolean {
    if (!equipment) {
      return false;
    }

    const canLogEquipmentIncident = this.canLogEquipmentIncident(equipment);
    const canLogEquipmentOperatingHours = this.canLogEquipmentOperatingHours(equipment);
    const canLogEquipmentMileage = this.canLogEquipmentMileage(equipment);
    const canLogEquipmentDamage = this.canLogEquipmentDamage(equipment);
    const canEditAnyEquipmentEventType = canLogEquipmentIncident || canLogEquipmentOperatingHours || canLogEquipmentMileage || canLogEquipmentDamage;

    return this.canViewEquipmentEvents(equipment) && canEditAnyEquipmentEventType;
  }

  public canViewEquipmentEvents(equipment: ViewEquipment | Nullable): boolean {
    if (!equipment) {
      return false;
    }

    const hasEquipmentEventManagementModule = this.authService.hasModule(Modules.EQUIPMENT_EVENT_MANAGEMENT);
    const hasEquipmentEventViewAuthority = this.authService.hasAuthority(Authorities.EQUIPMENT_EVENT_VIEW);

    return hasEquipmentEventManagementModule && hasEquipmentEventViewAuthority;
  }

  public canLogEquipmentIncident(equipment: Nullable<ViewEquipment>): boolean {
    const isActiveEquipment = this.isActiveEquipment(equipment);
    const canEdit = this.authService.hasAnyAuthority([
      Authorities.EQUIPMENT_ADD_LIFECYCLE_DATA,
      Authorities.EQUIPMENT_UPDATE,
      Authorities.EQUIPMENT_MANAGE_LIFECYCLE_EVENTS,
    ]);

    return isActiveEquipment && canEdit;
  }

  public canLogEquipmentOperatingHours(equipment: Nullable<ViewEquipment>): boolean {
    return this.allowManualLifecycle(equipment, Authorities.EQUIPMENT_MANAGE_OPERATING_HOURS);
  }

  public canLogEquipmentMileage(equipment: Nullable<ViewEquipment>): boolean {
    return Boolean(equipment) && this.allowManualLifecycle(equipment, Authorities.EQUIPMENT_MANAGE_MILEAGE);
  }

  public canLogEquipmentDamage(equipment: Nullable<ViewEquipment>): boolean {
    const isActiveEquipment = this.isActiveEquipment(equipment);
    const canEdit = this.authService.hasAnyAuthority([
      Authorities.EQUIPMENT_ADD_LIFECYCLE_DATA,
      Authorities.EQUIPMENT_UPDATE,
      Authorities.EQUIPMENT_MANAGE_DAMAGES,
    ]);

    return isActiveEquipment && canEdit;
  }

  private allowManualLifecycle(
    equipment: Nullable<ViewEquipment>,
    authority: Authorities.EQUIPMENT_MANAGE_OPERATING_HOURS | Authorities.EQUIPMENT_MANAGE_MILEAGE
  ): boolean {
    if (!equipment) return false;
    const isLiveData = {
      [Authorities.EQUIPMENT_MANAGE_OPERATING_HOURS]: equipment.hasLiveOperatingHours,
      [Authorities.EQUIPMENT_MANAGE_MILEAGE]: equipment.hasLiveMileage,
    }[authority];

    const canEdit = this.authService.hasAnyAuthority([
      Authorities.EQUIPMENT_ADD_LIFECYCLE_DATA,
      Authorities.EQUIPMENT_UPDATE,
      authority,
    ]);

    return (
      !isLiveData &&
      canEdit &&
      this.isActiveEquipment(equipment) &&
      !this.hasTelematicsLiveData(equipment)
    );
  }

  private hasTelematicsLiveData(equipment: ViewEquipment): boolean {
    return (
      this.isAssignedToTeltonikaTelematicUnit(equipment) &&
      this.hasEquipmentCurrentTelematicsData(equipment)
    );
  }

  private isAssignedToTeltonikaTelematicUnit(equipment: ViewEquipment): boolean {
    if (this.isViewEquipmentLinkedToTelematicsUnit(equipment)) {
      return [TelematicsUnitType.TELTONIKA_OBD_UNIT, TelematicsUnitType.TELTONIKA_CABLE_UNIT].includes(
        equipment?.assignedTelematicsUnits[0].key as TelematicsUnitType,
      );
    }
    return false;
  }

}
